import Db from './dexie/loader.js';
import { forEach } from 'lodash';
import { makeImage } from '@/apiConfig.ts'
import { store } from "@/store/index";

const uiThemeConfigName = 'uiThemeConfig',
    lightMode = 'light',
    darkMode = 'dark';

let activeMode = null;

const emptyTheme = {
    logo: null, // Changes by theme, so not set here
    logoOverDark: 'theme/empty-logo.png',
    logoOverLight: 'theme/empty-logo.png',

    // Textuals
    distributorName: '',
    footerText: 'All rights reserved.',

    // Colors
    lightColor: '#e0e0e0',
    darkColor: '#000', // Blackfon black

    // Color buttons
    btnTextColor: '#ffffff',
    accentColor: '#7a7a7a',
    alternateColor: '#b7b7b7',
}

const defaultBlackfonTheme = {
    logoOverDark: 'theme/Blackfon-White over black.png',
    logoOverLight: 'theme/Blackfon-Black over white.png',

    // Textuals
    distributorName: 'by Blackfon®',
    footerText: 'Blackfon. All rights reserved.',

    // Colors
    lightColor: '#e0e0e0',
    darkColor: '#000', // Blackfon black

    // Color buttons
    btnTextColor: '#ffffff',
    accentColor: '#f4711a',
    alternateColor: '#dcac8e',
}

const defaultTheme = { ...emptyTheme, ...defaultBlackfonTheme };

async function refresh() {
    // DOCUMENT is only available in browser environment
    if (typeof document == 'undefined')
        return;

    const updatedTheme = { ...defaultTheme };
    const dbTheme = await readThemeFromDb();

    if (dbTheme) {
        forEach(dbTheme, (value, key) => {
            if (_.has(updatedTheme, key))
                updatedTheme[key] = value;
        });
    }

    console.log("uiTheme.refresh.updatedTheme", updatedTheme);

    if (activeMode === darkMode) {
        updatedTheme.logo = updatedTheme.logoOverDark;
        document.body.style.backgroundColor = updatedTheme.darkColor;
        document.body.style.color = updatedTheme.lightColor;
        document.body.style.setProperty('--base-color', updatedTheme.darkColor);
        document.body.style.setProperty('--over-color', updatedTheme.lightColor);
    } else {
        updatedTheme.logo = updatedTheme.logoOverLight;
        document.body.style.backgroundColor = updatedTheme.lightColor;
        document.body.style.color = updatedTheme.darkColor;
        document.body.style.setProperty('--base-color', updatedTheme.lightColor);
        document.body.style.setProperty('--over-color', updatedTheme.darkColor);
    }

    if (updatedTheme.logo && updatedTheme.logo.includes('theme')) {
        updatedTheme.logo = `//${window.document.location.host}/${updatedTheme.logo}`;
    }

    document.body.style.setProperty('--primary', updatedTheme.accentColor);
    document.body.style.setProperty('--accent', updatedTheme.accentColor);
    document.body.style.setProperty('--secondary', updatedTheme.alternateColor);
    document.body.style.setProperty('--btn-text-color', updatedTheme.btnTextColor);


    // Actualización del título del documento
    document.title = `${updatedTheme.distributorName} - Portal Cautivo`;

    // UPDATE VUEX
    if (store) {
        store.commit('uiSetTheme', updatedTheme);
        console.log("uiTheme.refresh.Vuex store updated with theme:", updatedTheme);
    } else {
        console.warn("uiTheme.refresh.store not initialized yet, skipping Vuex update.");
    }

}

async function setUiThemeValues(cssUpdatingValues = {}) {

    let updatedTheme = { ...defaultTheme };

    console.log('uiTheme.Updating UI THEME CONFIG with values:', cssUpdatingValues);

    // CONFIG UPDATE
    forEach(defaultTheme, (value, key) => {
        if (_.has(cssUpdatingValues, key)) {
            console.log(`uiTheme.setUiThemeValues.updated ${key} to ${cssUpdatingValues[key]}`);
            updatedTheme[key] = cssUpdatingValues[key];
        }
    });

    console.log("uiTheme.setUiThemeValues.final updatedTheme:", updatedTheme);

    // REFRESH STYLE!
    return await writeDefaultThemeToDb(updatedTheme)
}

async function setUiDistValues(cssUpdatingDistValues = {}) {
    // If the distributor values are not complete, we fallback to the default blackfon theme to avoid breaking the UI with missing values.
    if(!cssUpdatingDistValues.color_accent)
        return await setUiThemeValues(defaultBlackfonTheme);

    const logoOverDark = makeImage(cssUpdatingDistValues.imagenes_id_logoOverDark, '800/180', 'logo.png'),
        logoOverLight = makeImage(cssUpdatingDistValues.imagenes_id_logoOverWhite, '800/180', 'logo.png');

    return await setUiThemeValues({
        logoOverDark,
        logoOverLight,
        distributorName: cssUpdatingDistValues.distribuidor,
        footerText: cssUpdatingDistValues.footer_text,
        lightColor: cssUpdatingDistValues.color_light,
        darkColor: cssUpdatingDistValues.color_dark,
        btnTextColor: cssUpdatingDistValues.color_btn_text,
        accentColor: cssUpdatingDistValues.color_accent,
        alternateColor: cssUpdatingDistValues.color_alternate,
    });
}

function getValue(valueName) {
    if (_.has(defaultTheme, valueName))
        return defaultTheme[valueName];
    else
        return null;
}

function getBaseColor(){
    return(activeMode === darkMode) ? store.getters.uiGetTheme.lightColor : store.getters.uiGetTheme.darkColor; // 'var(--base-color)';
}

function getBackgroundColor(){
    return(activeMode === darkMode) ?  store.getters.uiGetTheme.darkColor : store.getters.uiGetTheme.lightColo; // 'var(--over-color)';
}

function updateActiveMode() {
    activeMode = (
        typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
            ? darkMode
            : lightMode
    );
}

async function writeDefaultThemeToDb(updatedTheme) {
    Db.configs.where('key').equalsIgnoreCase(uiThemeConfigName).delete()
        .then(async () => {
            Db.configs.add({
                key: uiThemeConfigName,
                value: JSON.stringify(updatedTheme)
            });
            console.log("uiTheme.writeDefaultThemeToDb.success");
            await refresh();
            console.log("uiTheme.writeDefaultThemeToDb.success.refreshed");
            return true;
        })
        .catch((e) => {
            console.error("uiTheme.writeDefaultThemeToDb.error", e);
        });
}

async function readThemeFromDb() {
    try {
        let themeObj = await Db.configs.where('key').equalsIgnoreCase(uiThemeConfigName).first();
        if (_.isObjectLike(themeObj)) {
            let parsedTheme = JSON.parse(themeObj['value']);
            console.log("uiTheme.readThemeFromDb.success", parsedTheme);
            return parsedTheme;
        } else {
            throw 'uiTheme.readThemeFromDb.noThemeFound';
        }
    } catch (e) {
        console.warn("uiTheme.readThemeFromDb.error", e);
        return null;
    }
}

async function clearThemeInDb() {
    Db.configs.where('key').equalsIgnoreCase(uiThemeConfigName).delete()
        .then(async () => {
            console.log("uiTheme.clearThemeInDb.success");
            await refresh();
        })
        .catch((e) => {
            console.error("uiTheme.clearThemeInDb.error", e);
        });
}

function testUiTheme() {
    console.log("uiTheme.testUiTheme.starting test...");
    setUiThemeValues({
        accentColor: '#d5c48b',
        alternateColor: '#f1d683',
        btnTextColor: '#1a1a1a',
    });
    console.log("uiTheme.testUiTheme.test completed.");
}

function loadUp() {
    updateActiveMode();
    refresh();

    // Observe system color scheme changes
    if (typeof window !== 'undefined' && window.matchMedia) {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', () => {
                console.log('uiTheme.detected color scheme change, refreshing theme...');
                updateActiveMode();
                refresh();
            });
        } else {
            mediaQuery.addListener(() => {
                console.log('uiTheme.detected color scheme change, refreshing theme...');
                updateActiveMode();
                refresh();
            });
        }
    }
}

// Listen to system color scheme changes
// loadUp();
// testUiTheme();


export default {
    defaultTheme,
    activeMode,
    loadUp,
    clearThemeInDb,
    refresh,
    setUiThemeValues,
    setUiDistValues,
    getValue,
    getBaseColor,
    getBackgroundColor
};